import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { GridRowModel } from '@mui/x-data-grid';
import { Roles } from 'common/constants/roles';
import { useAuthContext } from 'common/contexts/AuthContext';
import { usePalmButtonEvaluator } from 'common/hooks/usePalmButtonEvaluator';
import { usePalmFetchEntities } from 'common/hooks/usePalmFetchEntities';
import { usePalmFilters } from 'common/hooks/usePalmFilters';
import { usePalmSortModel } from 'common/hooks/usePalmSortModel';

import { PalmDataGrid } from '../../common/components/PalmDataGrid';
import { fetchAuthorizedIssuers } from '../../common/services/api/AuthorizedIssuers/AuthorizedIssuers.thunk';
import {
    ColumnDefinition,
    ColumnType,
    Operations } from '../../common/types/TypeDefs';

import { AuthorizedIssuerCreateContent } from './AuthorizedIssuerCreateDrawerContent';
import { AuthorizedIssuerDeleteDialogContent } from './AuthorizedIssuerDeleteDialogContent';
import {
    addNewFilter,
    clearFilters,
    removeFilter,
    selectFilters } from './authorizedIssuersSlice';

export function AuthorizedIssuersTab() {

    const typeName = 'Authorized Issuer';
    const [processing, setProcessing] = useState<boolean>(false);
    const { currentUserRoles } = useAuthContext();
    const [sortModel, setSortModel] = usePalmSortModel('identityProviderName');
    const shouldDisableButtons = usePalmButtonEvaluator([ Roles.Admin, Roles.AppManager ]);

    const filterProp = usePalmFilters(
        selectFilters,
        addNewFilter,
        removeFilter,
        clearFilters);

    const params = useParams();
    const fetchEntities = usePalmFetchEntities(
        typeName,
        { tenantId: params.tenantId ?? '' },
        fetchAuthorizedIssuers,
        setProcessing);

    const disabledCallback = (row: GridRowModel) => {
        if (currentUserRoles.includes(Roles.Admin)) {
            return false;
        }
        if (currentUserRoles.includes(Roles.AppManager) && !row.isGlobal) {
            return false;
        }
        return true;
    };

    const actionDefinitions = [
        { action: Operations.Delete, disabled: disabledCallback }
    ];

    const columnDefinitions: ColumnDefinition[] = [
        { columnId: 'identityProviderName', columnName: 'Identity Provider', columnType: ColumnType.First, copy: false },
        { columnId: 'audience', columnName: 'Authorized Issuer Audience', columnType: ColumnType.Tooltip, copy: true },
        { columnId: 'actions', columnName: 'Action', columnType: ColumnType.Action }
    ];

    return <PalmDataGrid
        title={typeName}
        processing={processing}
        actionDefinitions={actionDefinitions}
        columnDefinitions={columnDefinitions}
        deleteFunction={AuthorizedIssuerDeleteDialogContent}
        isCreateButtonDisabled={shouldDisableButtons}
        filter={filterProp}
        contentFunction={AuthorizedIssuerCreateContent}
        refreshDataGridClick={() => fetchEntities()}
        sortModel={sortModel}
        clientSortHandler={setSortModel}
        shouldShowExportButton={true}
    />;
}
